// Copyright (c) Wiens Consulting. All rights reserved.

/** Effective dates are calendar dates, not instants to shift into the viewer's timezone. */
export function formatProgramStartDate(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  })
}

export interface GuidelineDraft {
  householdSize: string
  monthlyIncomeLimit: string
}

export interface ProgramSettingsDraftValidation {
  startDate: string
  guidelines: GuidelineDraft[]
  minimumBenefit: string
  maximumBenefit: string
}

function numberValue(value: string, label: string): number {
  if (value.trim() === "") throw new Error(`${label} is required.`)
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) throw new Error(`${label} must be a valid number.`)
  return parsed
}

/** Parses the part of the settings draft shared by the editor and its validation tests. */
export function parseProgramSettingsDraft(draft: ProgramSettingsDraftValidation) {
  if (!draft.startDate) throw new Error("Start date is required.")
  const guidelines = draft.guidelines.map((row) => ({
    householdSize: numberValue(row.householdSize, "Household size"),
    monthlyIncomeLimit: numberValue(row.monthlyIncomeLimit, "Monthly income limit"),
  }))
  const sizes = new Set(guidelines.map((row) => row.householdSize))
  if (sizes.size !== guidelines.length) throw new Error("Each household size may appear only once.")
  if (guidelines.some((row) => !Number.isInteger(row.householdSize) || row.householdSize < 0 || row.householdSize > 24)) {
    throw new Error("Household size must be a whole number from 0 through 24.")
  }

  const minimumBenefit = numberValue(draft.minimumBenefit, "Minimum benefit")
  const maximumBenefit = numberValue(draft.maximumBenefit, "Maximum benefit")
  if (minimumBenefit > maximumBenefit) throw new Error("Minimum benefit cannot exceed maximum benefit.")

  return { guidelines, minimumBenefit, maximumBenefit }
}
